import {ResourceException} from "../types/resourceError";
import {FailedResource, LoadingResource, Resource, SucceededResource, UninitializedResource} from "../types/resource";

export const isResourceUninitialized = <T>(resource: Resource<T>): resource is UninitializedResource =>
    resource.state === "uninitialized";

export const isResourceLoading = <T>(resource: Resource<T>): resource is LoadingResource =>
    resource.state === "loading";

export const isResourceSucceeded = <T>(resource: Resource<T>): resource is SucceededResource<T> =>
    resource.state === "hasData";

export const isResourceFailed = <T>(resource: Resource<T>): resource is FailedResource<T> =>
    resource.state === "hasError";

export const isAnyResourceFailed = (resources: Resource<unknown>[]) => resources.some((r) => r.state === "hasError");

export const getResourceError = <T>(resource: Resource<T>) =>
    isResourceFailed(resource) ? (resource.error as unknown as ResourceException).resourceError : undefined;

export const getResourceErrorMessage = <T>(resource: Resource<T>) =>
    isResourceFailed(resource) ? (resource.error as unknown as ResourceException).resourceError.message : undefined;

export const getFirstFailedResourceErrorMessage = (resources: Resource<unknown>[]) => {
    const failedResource = resources.find((r) => isResourceFailed(r)) as FailedResource | undefined;

    return failedResource ? (failedResource.error as ResourceException).resourceError.message : undefined;
};

/**
 * Function executing given async function and calling provided callback function on reactive resource updates
 */
export const fetchResource = async <TData>({
    fetchFn,
    onResourceChange,
}: {
    fetchFn: () => Promise<TData>;
    onResourceChange: (resource: Resource<Promise<TData>>) => void;
}) => {
    onResourceChange({
        state: "loading",
    });

    try {
        const data = await fetchFn();

        onResourceChange({
            state: "hasData",
            data,
        });

        return data;
    } catch (error: unknown) {
        onResourceChange({
            state: "hasError",
            error: error as ResourceException,
        });

        throw error;
    }
};
